import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  CarbonStackedLineChart,
  TransitionsEmissionsChart,
  SettlementDestinationEmissionsChart,
  Emissions_Lost_Forest,
  ParallelChart
} from "../charts/charts";
import "./SectionThree.css";
import CollapsibleCitation from "../tools/CollapsibleCitation";
import InformationPopUp from "../tools/InformationPopUp";

export default function EmisionesCarbono() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.5, once: false });

  // helper for staggering
  const animateProps = (delay = 0) => ({
    initial: { opacity: 0, y: 10 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay }
  });

  return (
    <section
      ref={sectionRef}
      className="h-screen snap-start bg-gradient-to-b from-[#324130] to-[#92843B] text-white"
    >
      <div className="h-full p-6" style={{ marginTop: "20vh" }}>
        {/* <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-4xl font-bold mb-6"
        >
          EMISIONES DE CARBONO
        </motion.h2>
 */}
        {/* Two-column layout */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2" style={{ marginTop: "5vh" }}>
          {/* LEFT column: two line charts */}
          <div className="flex flex-col h-screen p-6" >
            {/* Urbanization emission line */}
            <motion.div {...animateProps(0.2)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl font-semibold mb-2"
              >
                Emisiones de CO₂ causadas por nuevo suelo urbanizado
                <InformationPopUp title="Emisiones de CO₂ causadas por nuevo suelo urbanizado"
                description="En esta gráfica, se muestra el dióxido de carbono total emitido por año cuando un tipo de suelo previamente no construido se urbaniza."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                  source="Elaboración propia con base en SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <SettlementDestinationEmissionsChart />
              </div>
            </motion.div>

            {/* Forest-loss emission line */}
            <motion.div {...animateProps(0.6)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
                Emisiones de CO₂ causadas por perdida de superficie forestal
                <InformationPopUp title="Emisiones de CO₂ causadas por perdida de superficie forestal"
                description="Similar a la gráfica anterior, esta muestra el CO2 emitido cuando bosques (tanto primarios como secundarios) se convierten en cualquier otro tipo de suelo"
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                  source="Elaboración propia con base en SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <Emissions_Lost_Forest />
              </div>
              
              
            </motion.div>
          </div>
          {/* New RIGHT column: emissions stacked bar chart */}

          <div className="flex flex-col h-screen p-6" >
            {/* Línea de emisiones por urbanización */}
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
              Balance de captura y emisiones de CO₂
              <InformationPopUp title="Balance de captura y emisiones de CO₂"
                description="A grandes rasgos, las emisiones de CO2 son producidas por <b>cuatro</b> mecánicas distintas: <br />
                <ol>
<li><b>1. Bosques</b>: Debido a su alto contenido de vegetación, los bosques capturan una gran cantidad de carbono de la atmósfera. </li>
<br />
<li><b>2. Transiciones de uso de suelo</b>: Todos los tipos de suelo tienen una cierta cantidad de biomasa asociada; en pocas palabras, esta es la masa total de todos los organismos que viven dentro de él. Por ejemplo, un <b>bosque</b> tiene una biomasa muy alta, mientras que la del <b>suelo urbanizado</b> es baja. 
Todos los organismos vivos almacenan una cierta cantidad de carbono dentro de ellos, el cual es expedido a la atmósfera cuando estos mueren y decaen. Si un área con una gran cantidad de biomasa transiciona a un tipo de suelo con una biomasa asociada menor (por ejemplo, si un bosque se vuelve un pastizal), se emitirá una cantidad de carbono proporcional a la diferencia de biomasa entre ambos tipos de uso de suelo, y el área total.
Es posible que este proceso ocurra de manera inversa: si un área de baja biomasa se vuelve una de alta, se absorberá carbono de la atmósfera.</li>
<br />
<li><b>3. Mineralización y suelos orgánicos</b>: La biomasa no solo se encuentra en los animales y plantas; el mismo suelo está repleto de microorganismos y materia orgánica en descomposición. Cuando una zona pierde biomasa (por ejemplo, si se tala un bosque), el suelo también se ve afectado, y se vuelve árido y seco. A este proceso se le conoce como mineralización.</li>
<br />
<li><b>4. Equivalentes de N2O</b>: Los dos puntos anteriores estiman las emisiones producidas en toneladas de dióxido de carbono (CO2). Sin embargo, este no es el único gas de efecto invernadero producido por estos procesos; otro de suma importancia es el óxido nitroso (N2O). De acuerdo a estimaciones realizadas por el IPCC, 1 tonelada de N2O es equivalente a 280 toneladas de CO2. El modelo de SISEPUEDE estima las emisiones de N2O, y reporta su contribución utilizando esta equivalencia.</li>
</ol>
Esta gráfica muestra el carbono absorbido y emitido a través de cada una de estas mecánicas, y el resultado total."
bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                  source="Elaboración propia con base en SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <CarbonStackedLineChart />
              </div>
            </motion.div>

            <motion.div {...animateProps(1.0)}></motion.div>
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-xl font-semibold mb-2"
              >
                Potencial de captura de CO₂ perdido y área urbanizada
                <InformationPopUp title="Potencial de captura de CO₂ perdido y área urbanizada"
                description="De las <b>11 categorías</b> de uso de suelo utilizadas en el modelo, <b>3 de ellas</b> son originadas por intervención humana:
                
                <ul>

                <li><b>·Cultivos (croplands)</b></li>

                <li><b>·Pastizales para ganado (pastures)</b></li>

                <li><b>·Urbanización (settlements)</b></li>

                </ul>

                En esta gráfica, se muestra el CO2 emitido cuando un área se convierte en alguna de estas tres clases."       
                  bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                  source="Elaboración propia con base en SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <ParallelChart />
              </div>

                            
            </motion.div>
          </div>
          
        </div>
      </div>
    </section>
  );
}